import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import axiosConstants from "./axiosConstants";

const settings = {
  headers: {
    "Content-Type": "application/json",
    "Access-Control-Allow-Headers": "*",
    "Access-Control-Allow-Origin": "*",
    "Access-Control-Allow-Credentials": true,
    "x-interswitch-domain": "ISW",
    "X-XSRF-TOKEN": axiosConstants.XSRF
  }
};

interface AxiosInstanceType {
  get: (url: string, request?: AxiosRequestConfig) => Promise<AxiosResponse>;
  post: (
    url: string,
    data?: any,
    request?: AxiosRequestConfig
  ) => Promise<AxiosResponse>;
  put: (
    url: string,
    data?: any,
    request?: AxiosRequestConfig
  ) => Promise<AxiosResponse>;
  patch: (
    url: string,
    data?: any,
    request?: AxiosRequestConfig
  ) => Promise<AxiosResponse>;
  delete: (url: string, request?: AxiosRequestConfig) => Promise<AxiosResponse>;
}

const getAxiosInstance = (
  _instance: AxiosInstance,
  getAdditionalHeaders?: () => Record<string, string>
) => {
  return {
    async get(url: string, config?: AxiosRequestConfig) {
      try {
        const response = await _instance.get(url, {
          ...(config || {}),
          headers: {
            ...(getAdditionalHeaders?.() || {}),
            ...(config?.headers || {})
          }
        });
        return await Promise.resolve(response);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async post(url: string, data: any, config?: AxiosRequestConfig) {
      try {
        const response = await _instance.post(url, data, {
          ...(config || {}),
          headers: {
            ...(getAdditionalHeaders?.() || {}),
            ...(config?.headers || {})
          }
        });
        return await Promise.resolve(response);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async put(url: string, data: any, config?: AxiosRequestConfig) {
      try {
        const response = await _instance.put(url, data, {
          ...(config || {}),
          headers: {
            ...(getAdditionalHeaders?.() || {}),
            ...(config?.headers || {})
          }
        });
        return await Promise.resolve(response);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async patch(url: string, data: any, config?: AxiosRequestConfig) {
      try {
        const response = await _instance.patch(url, data, {
          ...(config || {}),
          headers: {
            ...(getAdditionalHeaders?.() || {}),
            ...(config?.headers || {})
          }
        });
        return await Promise.resolve(response);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async delete(url: string, config?: AxiosRequestConfig) {
      try {
        const response = await _instance.delete(url, {
          ...config,
          headers: {
            ...(getAdditionalHeaders?.() || {}),
            ...(config?.headers || {})
          }
        });
        return await Promise.resolve(response);
      } catch (error) {
        return Promise.reject(error);
      }
    }
  };
};

const baseURLTasks =
  process.env.NODE_ENV === "production"
    ? "back-office-portlet/"
    : "https://back-office-service.k8.isw.la/back-office-service/api/v1";
const instanceTasks = axios.create({
  ...settings,
  baseURL: baseURLTasks
});

if (process.env.NODE_ENV !== "production") {
  instanceTasks.defaults.headers.common.Authorization =
    "Bearer eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCJ9.eyJsYXN0TmFtZSI6IklmZWFueWktT2tvcm9ua3dvIiwibWVyY2hhbnRfY29kZSI6Ik1YMTU3ODUiLCJmaXJzdExvZ2luIjpmYWxzZSwidXNlcl9uYW1lIjoiVG9jaGkuT2tvcm9ua3dvIiwicmVxdWVzdG9yX2lkIjoiMTIzOTg0ODQ3NzMiLCJjbGllbnRfdHlwZSI6IkFMUEhBIiwiY2xpZW50X2lkIjoiaXN3LXBvcnRhbCIsImluc3RpdHV0aW9uX2lkIjoiMTEzNSIsImNvcmVfaWQiOiIxMTUxNyIsImNsaWVudF9hdXRob3JpemF0aW9uX2RvbWFpbiI6IklTVyIsInNjb3BlIjpbInByb2ZpbGUiXSwiZXhwIjoxNzY0MDExOTE1LCJjbGllbnRfbmFtZSI6IklTVyBCdXNpbmVzcyBQb3J0YWwiLCJqdGkiOiJmYTg3NmU3Zi1iNTBkLTQ5ZjYtYTE3YS1iYTZjY2RiN2UyY2YiLCJlbWFpbCI6IlRvY2hpLklmZWFueWktT2tvcm9ua3dvQGludGVyc3dpdGNoZ3JvdXAuY29tIiwicGFzc3BvcnRJZCI6IjI4QzA0OUQzLUVDQjMtMzhDQy1BNTE4LTg5RjJBRThFMENCNCIsImRvbWFpbl9jb2RlIjoiSVNXIiwibW9iaWxlTm8iOiIyMzQ4MTMzMjEzMTk4IiwiYXBwbGljYXRpb25faWQiOiI1IiwicGF5YWJsZV9pZCI6IjM2MDQ0IiwiZmlyc3ROYW1lIjoiVG9jaGkiLCJlbWFpbFZlcmlmaWVkIjp0cnVlLCJhdWQiOlsiaW5jb2duaXRvIiwiY2Flc2FyIiwidHJhbnNmZXItc2VydmljZS1jb3JlIiwiYXJiaXRlci1pc3cta2UiLCJuaWJzcy1uZWZ0LWFwaSIsImlzdy1wYXltZW50Z2F0ZXdheSIsInRyYW5zZmVyLXNlcnZpY2UtcG9ydGxldCIsImlzdy1pbnN0aXR1dGlvbiIsInByb2plY3QteC1jb25zdW1lciIsInBhc3Nwb3J0IiwiaXN3LXBvc3Qtb2ZmaWNlIiwiaXN3LWNvbGxlY3Rpb25zIiwidmF1bHQiLCJpc3ctY29yZSIsImlzdy1wb3J0YWwiLCJvcHMtY2VudHJhbCIsImlzdy1wYXBzcyIsInNjcCIsInNtYXJ0Y2FyZHByb2Nlc3MiLCJyZWN1cnJlbnQtYmlsbGluZy1hcGkiLCJoaW1zLXBvcnRsZXQiLCJ0cy1uYW1lLWVucXVpcnkiLCJuaWJzcy1pZHJpcy1jb3B5LXNldHRpbmdzIiwiY2FyZGxlc3Mtc2VydmljZSIsInBheXBob25lLWVjYXNoLWV4dHJhY3RvciIsImlzdy1idnMtY29yZSIsImFyYml0ZXIiLCJyZWZlcnJhbC1zZXJ2aWNlLWFwaSIsInByb2plY3QteC1tZXJjaGFudCIsInRyYW5zZmVyLXNlcnZpY2UtYWRtaW4iLCJ3ZWJwYXktcG9ydGxldCIsImlzdy1sZW5kaW5nLXNlcnZpY2UiLCJ0dnAtYm9va2luZy1zZXJ2aWNlIl0sImF1dGhlbnRpY2F0ZWRCeSI6IkxEQVAiLCJtb2JpbGVOb1ZlcmlmaWVkIjp0cnVlfQ.cSVViwFgovocIItdHkycA7v876_DVNUX1N3WeI9RCFn-rXAyTOC8fCpxmUhYLxlMeXn-N3UnRsYUiwyxJeVCZ7mt492KU63JvFw4uqZF9aiOh2TQSsTIqM0yizGIJ0Rql0A8eSZEjABK6_4iX6mYgIXPI0tjHgqqnleIEgBK7Zix4cRlCL6mbEsBmIlbj4TKz5x3-sc2CMVbinkCQN5r6xpowGtJ8fl_eRgyou51biRZGuHc_zFJwqj6fCnQfUMbXxkjtXV0iIj3T1TWq8j8jojCZDmJYFrZyYwo6s17XsKE_oknu3oCdyAdLvcKCLu13RpqNW73bRJUiYBmyaKNKw";
}

export const axiosInstanceTasks: AxiosInstanceType =
  getAxiosInstance(instanceTasks);
